import { useEffect, useState } from "react"
import { apiFetch } from "../../lib/api"
import { useEmpresa } from "../../app/context/EmpresaContext"

type AuditRow = {
  id: string
  criado_em: string
  acao: string
  resultado: string
  recurso_tipo: string | null
  recurso_id: string | null
  ator_tipo: string | null
  detalhes: Record<string, unknown> | null
}

const formatWhen = (iso: string) =>
  new Date(iso).toLocaleString("pt-BR", {
    timeZone: "America/Sao_Paulo",
    dateStyle: "short",
    timeStyle: "medium",
  })

const resultadoClass = (resultado: string) => {
  if (resultado === "sucesso") return "text-emerald-700"
  if (resultado === "falha" || resultado === "erro") return "text-red-700"
  return "text-slate-700"
}

export const AuditoriaPage = () => {
  const { empresaId } = useEmpresa()
  const [rows, setRows] = useState<AuditRow[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  useEffect(() => {
    if (!empresaId) {
      setRows([])
      return
    }
    setLoading(true)
    setError("")
    apiFetch<AuditRow[]>(`/v1/empresas/${empresaId}/auditoria?limit=100`)
      .then(setRows)
      .catch((err) => {
        setRows([])
        setError(err instanceof Error ? err.message : "Erro ao carregar auditoria")
      })
      .finally(() => setLoading(false))
  }, [empresaId])

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Auditoria</h1>
        <p className="mt-1 text-sm text-slate-600">
          Trilha de ações na <strong>empresa ativa</strong> (NF-e, certificados, webhooks, gestão).
        </p>
      </div>

      {!empresaId && (
        <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Selecione uma empresa em <strong>Empresa ativa</strong> na barra lateral.
        </p>
      )}

      {error ? (
        <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900" role="alert">
          {error}
        </p>
      ) : null}

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3 font-medium">Quando</th>
              <th className="px-4 py-3 font-medium">Ação</th>
              <th className="px-4 py-3 font-medium">Recurso</th>
              <th className="px-4 py-3 font-medium">Ator</th>
              <th className="px-4 py-3 font-medium">Resultado</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                  Carregando…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-slate-600">
                  {empresaId
                    ? "Nenhum evento registrado para esta empresa. Novas ações no painel passam a aparecer aqui."
                    : "—"}
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.id} className="align-top">
                  <td className="whitespace-nowrap px-4 py-3 text-slate-800">{formatWhen(r.criado_em)}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">{r.acao}</td>
                  <td className="px-4 py-3 text-slate-700">
                    <p>{r.recurso_tipo ?? "—"}</p>
                    {r.recurso_id ? <p className="mt-0.5 font-mono text-xs text-slate-500">{r.recurso_id}</p> : null}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{r.ator_tipo ?? "—"}</td>
                  <td className={`px-4 py-3 font-medium ${resultadoClass(r.resultado)}`}>{r.resultado}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
